import { useRef, useState } from 'react'
import { recognizeIntro, type IntroKamerakontext } from '../lib/api'
import { framesFromFile, listeVon } from '../lib/imageAnalysis'
import { vibrate, VIBRATION_ERKANNT } from '../lib/haptics'

/** Länger wartet die Erkennung nicht — danach gilt: nichts erkannt. */
const ERKENNUNG_MS = 20_000

/**
 * Ein Foto in den Intro-Chat: am Handy aufgenommen oder aus der Mediathek
 * gewählt (das Handy fragt selbst, welches von beiden). Der Bildschirm zeigt
 * es groß über der Frage.
 *
 * Wie die Kamera im gewöhnlichen Chat („Was siehst du gerade?") zählt nur ein
 * erkannter Showcase: Dann erklärt K4ia ihn („Ich stehe vor: …"), bei
 * mehreren fragt sie nach. Erkennt sie keinen, geht nichts an den Bildschirm —
 * das Handy sagt es an Ort und Stelle. Eine Bildbeschreibung gibt es bewusst
 * nicht. Steht im Eingabefeld schon eine Frage, geht sie statt „Ich stehe
 * vor: …" mit dem erkannten Showcase.
 *
 * Erkannt wird über `POST /api/v1/intro/vision/<token>` — das Handy hat im
 * Intro bewusst kein Sitzungs-Cookie. Gespeichert wird das Foto nirgends; der
 * Bildschirm bekommt es nur, solange der Chat läuft (FR-34).
 */
export function useIntroFoto({
  token,
  titelVon,
  onFrage,
}: {
  token: string
  /** Titel der Live-Showcases — dieselben wie die Boxen. */
  titelVon: Map<string, string>
  onFrage: (frage: string, kontext: IntroKamerakontext) => Promise<void>
}) {
  const eingabe = useRef<HTMLInputElement>(null)
  /** Vorschau, solange K4ia noch schaut, ob ein Showcase darauf ist. */
  const [schaut, setSchaut] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  /** `true`, wenn etwas in den Chat ging. */
  async function senden(datei: File, eigeneFrage: string): Promise<boolean> {
    setFehler(null)
    let bilder: { klein: string; gross: string }
    try {
      bilder = await framesFromFile(datei)
    } catch {
      setFehler('Das Foto ließ sich nicht öffnen. Versuch es bitte mit einem anderen.')
      return false
    }
    setSchaut(bilder.klein)
    let frage: string | null = null
    const kontext: IntroKamerakontext = { imageDataUrl: bilder.gross }
    try {
      // Gründlich, wie der Knopf im gewöhnlichen Chat: Sieht das kleine Modell
      // nichts, schaut das große — es gibt keinen zweiten Versuch danach.
      const e = await recognizeIntro(token, bilder.klein, {
        source: 'photo',
        stage2: 'strong',
        timeoutMs: ERKENNUNG_MS,
      })
      if (e.showcaseId) {
        vibrate(VIBRATION_ERKANNT)
        kontext.showcaseId = e.showcaseId
        frage = eigeneFrage || `Ich stehe vor: ${titelVon.get(e.showcaseId) ?? 'diesem Showcase'}`
      } else if (e.alternatives.length >= 2) {
        vibrate(VIBRATION_ERKANNT)
        kontext.showcaseIds = e.alternatives.map((a) => a.showcaseId)
        frage =
          eigeneFrage ||
          `Ich stehe vor: ${listeVon(kontext.showcaseIds.map((id) => titelVon.get(id) ?? 'ein Showcase'))}`
      }
    } catch {
      // Zeit vorbei oder Fehler — zählt wie nichts erkannt.
    } finally {
      setSchaut(null)
    }
    if (!frage) {
      setFehler(
        'Auf dem Foto erkenne ich keinen Showcase. Geh etwas näher ran oder ändere den Winkel.',
      )
      return false
    }
    await onFrage(frage, kontext)
    return true
  }

  return {
    schaut,
    fehler,
    /** Öffnet Kamera bzw. Mediathek des Handys. */
    waehlen: () => eingabe.current?.click(),
    /** Das versteckte Dateifeld; `frage` liefert, was im Eingabefeld steht, `leeren` räumt es nach dem Senden. */
    feld: (frage: () => string, leeren: () => void) => (
      <input
        ref={eingabe}
        type="file"
        accept="image/*"
        className="hidden"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => {
          const datei = e.target.files?.[0]
          e.target.value = ''
          if (datei) void senden(datei, frage()).then((ok) => ok && leeren())
        }}
      />
    ),
  }
}
